import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { decidePass, listPasses, passAction, updatePassSettings } from '../../../../api/visitor-passes-client';
import type { PassListResponse, PassState, VisitorPass } from '../../../../interfaces/visitor-pass';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { StatusPill, StatusTone } from '../../../status-pill/status-pill';
import { PassDetailModal } from '../../../pass-editor/pass-detail-modal';
import { readPassError } from '../../../pass-editor/pass-editor';
import { ListSkeleton, LoadError, skeletonHeight } from '../dashboard-shared-components/load-state';
import { Presence, Staleable, StateSwitch, StatusLabel } from '../../../../motion';
import {
  BodyRow,
  Table,
  TableCaption,
  TableFrame,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../../table/list-table';
import { TypeFilterSelect } from '../dashboard-users-components/dashboard-users.styled';
import { groupByPortfolio, useBuildings, useRememberedBuilding } from '../dashboard-units-components/units-shared';
import toast from '../../../../utils/toast';
import { describeSchedule, describeVisit, formatPassDate } from '../../../../utils/pass-schedule';
import { zoneLabel } from '../../../../function-utils/building-time';
import {
  Empty,
  EmptyText,
  EmptyTitle,
  Footer,
  FooterNote,
  Lead,
  NameButton,
  RowActions,
  Screen,
  Sub,
  SwitchCard,
  SwitchHint,
  SwitchLabel,
  Toolbar,
  ToolbarField,
  ToolbarLabel,
} from './dashboard-passes.styled';

/**
 * VISITOR PASSES — "who has standing access to my building" (resident-portal
 * Plan 3, RP-3.7). /dashboard/visitor-passes.
 *
 * Every pass at one building: who, for which unit and resident, the schedule in
 * one sentence, whether the building or a resident added it, the next visit and
 * its state. Live or Ended, paged with the server's full-precision cursor. The
 * row name opens the pass (PassDetailModal); Pause / Resume / End sit on the
 * row for whoever the SERVER says may do them (`can_edit` / `can_end` on every
 * pass — a manager or the owner, and a desk the building lets write).
 *
 * THE SWITCH lives here (UI-CONTRACT §6), not in the building edit form: that
 * route is building:edit and multipart, and property managers hold neither. It
 * is for visitorpass:manage only, and turning it OFF asks first — it hides every
 * pass from the front desk until it is turned back on.
 *
 * APPROVAL MODE (migration 125, RP-4.9). A second switch for managers: "A
 * manager approves passes residents add". While on, a resident's new pass is
 * pending — nobody is expected — until a manager Approves it or Declines it
 * (with an optional note to the household). "Awaiting approval" lists them,
 * with the count; the bell's link opens it (?state=awaiting). Approve /
 * Decline sit on the row only where the SERVER says `can_decide`.
 *
 * THE DESK'S COPY (owner decision 2026-09-29): /visitor-log/passes renders this
 * same register with `fixedBuilding` — the building the desk is working in, no
 * picker. Nothing else changes: the switch is still visitorpass:manage only, and
 * the row actions are still whatever the server says each pass allows.
 */

type ListState = 'live' | 'ended' | 'awaiting';

// The bell's link lands on the awaiting list (visitor_pass.pending).
const initialState = (): ListState => {
  try {
    return new URLSearchParams(window.location.search).get('state') === 'awaiting' ? 'awaiting' : 'live';
  } catch {
    return 'live';
  }
};

const PAGE = 50;

const TONES: Record<PassState, StatusTone> = {
  active: 'success',
  paused: 'warning',
  scheduled: 'warning',
  ended: 'neutral',
  pending: 'warning',
};

const stateLabel = (pass: VisitorPass): string => {
  switch (pass.state) {
    case 'active':
      return 'Active';
    case 'pending':
      return 'Waiting for approval';
    case 'paused':
      return pass.paused_until
        ? `Paused until ${formatPassDate(pass.paused_until, { day: 'numeric', month: 'short' })}`
        : 'Paused';
    case 'scheduled':
      return `Starts ${formatPassDate(pass.valid_from, { day: 'numeric', month: 'short' })}`;
    default:
      return 'Ended';
  }
};

const nameOf = (pass: VisitorPass): string =>
  [pass.first_name, pass.last_name].filter(Boolean).join(' ') || 'Visitor';
const firstOf = (pass: VisitorPass): string => pass.first_name || nameOf(pass);
const hostOf = (pass: VisitorPass): string =>
  [pass.host?.first_name, pass.host?.last_name].filter(Boolean).join(' ') || '—';
const possessive = (name: string): string => `${name}'s`;

type Confirming = { kind: 'pause' | 'end' | 'decline'; pass: VisitorPass } | null;

export interface DashboardPassesProps {
  /** The desk's register: this one building, no building picker. */
  fixedBuilding?: { id: number; name: string };
}

export const DashboardPasses: React.FC<DashboardPassesProps> = ({ fixedBuilding }) => {
  const { permissions } = useAuth();
  const canManage = can(permissions, PERMISSIONS.VISITORPASS_MANAGE);

  const { buildings, loading: buildingsLoading, error: buildingsError } = useBuildings();
  const groups = useMemo(() => groupByPortfolio(buildings), [buildings]);
  const [rememberedId, setBuildingId] = useRememberedBuilding();
  const buildingId = fixedBuilding ? fixedBuilding.id : rememberedId;
  const building = fixedBuilding
    ? { name: fixedBuilding.name }
    : buildings.find(b => b.id === buildingId) || null;
  const [listState, setListState] = useState<ListState>(initialState);
  const [declineNote, setDeclineNote] = useState('');
  const [approving, setApproving] = useState<string | null>(null);

  const [data, setData] = useState<PassListResponse | null>(null);
  const [rows, setRows] = useState<VisitorPass[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const seq = useRef(0);
  // Another building or another tab is not this list's stale data: it starts
  // from a skeleton. A reload of the same one keeps its rows and dims them.
  const loadedFor = useRef('');

  const [openPassId, setOpenPassId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [resuming, setResuming] = useState<string | null>(null);
  const inFlight = useRef(new Set<string>());

  const [switchBusy, setSwitchBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  // A remembered building the reader can no longer reach falls back to the first.
  useEffect(() => {
    if (fixedBuilding || buildingsLoading || buildings.length === 0) return;
    if (!buildings.some(b => b.id === buildingId)) setBuildingId(buildings[0].id);
  }, [fixedBuilding, buildings, buildingsLoading, buildingId, setBuildingId]);

  const load = useCallback(() => {
    if (!buildingId) return;
    seq.current += 1;
    const mine = seq.current;
    const key = `${buildingId}:${listState}`;
    if (loadedFor.current !== key) {
      loadedFor.current = key;
      setData(null);
      setRows([]);
    }
    setLoading(true);
    setFailed(null);
    setMoreFailed(false);
    listPasses(buildingId, { state: listState, limit: PAGE })
      .then(response => {
        if (seq.current !== mine) return;
        setData(response);
        setRows(response.rows);
      })
      .catch(() => {
        if (seq.current === mine) setFailed('Could not load the visitor passes for this building.');
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [buildingId, listState]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = () => {
    if (!buildingId || !data?.next_cursor || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    setMoreFailed(false);
    listPasses(buildingId, { state: listState, limit: PAGE, cursor: data.next_cursor })
      .then(response => {
        if (seq.current !== mine) return;
        setData(response);
        setRows(current => {
          const seen = new Set(current.map(r => r.pass_id));

          return [...current, ...response.rows.filter(r => !seen.has(r.pass_id))];
        });
      })
      .catch(() => {
        if (seq.current === mine) setMoreFailed(true);
      })
      .finally(() => {
        if (seq.current === mine) setLoadingMore(false);
      });
  };

  /* Pause, resume and end. One request per pass; the list is read again
     whatever the answer, so a row never shows a state the server does not hold.
     Toasts report the OUTCOME in our words, never the server's text. */
  const runAction = async (pass: VisitorPass, action: 'pause' | 'resume' | 'revoke') => {
    if (inFlight.current.has(pass.pass_id)) return;
    inFlight.current.add(pass.pass_id);
    const first = firstOf(pass);
    try {
      await passAction(pass.pass_id, action);
      toast.success(
        action === 'pause'
          ? `${possessive(first)} pass is paused.`
          : action === 'resume'
          ? `${possessive(first)} pass is active again.`
          : `${possessive(first)} pass has ended.`
      );
    } catch (error) {
      const verb = action === 'revoke' ? 'end' : action;
      const fallback = `Could not ${verb} ${possessive(first)} pass. Try again.`;
      const reading = readPassError(error, {
        limits: data?.limits,
        fallback,
        notFound: 'This pass has already ended.',
      });
      toast.error(reading.status === 400 ? fallback : reading.message);
    } finally {
      inFlight.current.delete(pass.pass_id);
      load();
    }
  };

  /* Approve (no confirm: it is what the resident asked for, and it can be
     ended afterwards) and Decline (asks first, with an optional note). */
  const decide = async (pass: VisitorPass, decision: 'approve' | 'reject', note?: string) => {
    if (inFlight.current.has(pass.pass_id)) return;
    inFlight.current.add(pass.pass_id);
    const first = firstOf(pass);
    try {
      // What this row SHOWED goes with the answer: a request the household
      // edited in the meantime is not approved unseen (409 PASS_CHANGED).
      await decidePass(pass.pass_id, decision, note, pass.updated_at);
      toast.success(
        decision === 'approve'
          ? `${possessive(first)} pass is approved. The household is told.`
          : `${possessive(first)} pass is declined. The household is told.`
      );
    } catch (error) {
      const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
      toast.error(
        code === 'PASS_DECIDED'
          ? `${possessive(first)} pass has already been answered.`
          : code === 'PASS_CHANGED'
          ? 'This request changed since you opened it. Read it again.'
          : `Could not ${decision === 'approve' ? 'approve' : 'decline'} ${possessive(first)} pass. Try again.`
      );
    } finally {
      inFlight.current.delete(pass.pass_id);
      load();
    }
  };

  const setApproval = async (approval: boolean) => {
    if (!buildingId || switchBusy) return;
    setSwitchBusy(true);
    try {
      const saved = await updatePassSettings(buildingId, { approval });
      setData(current => (current ? { ...current, approval_required: saved.visitor_pass_approval === true } : current));
      toast.success(
        saved.visitor_pass_approval
          ? 'A manager now approves every visitor pass a resident adds here.'
          : 'Residents’ visitor passes no longer need approval here.'
      );
    } catch {
      toast.error('Could not change the approval setting. Try again.');
    } finally {
      setSwitchBusy(false);
      load();
    }
  };

  const setSwitch = async (enabled: boolean) => {
    if (!buildingId || switchBusy) return;
    setSwitchBusy(true);
    try {
      const saved = await updatePassSettings(buildingId, { enabled });
      setData(current => (current ? { ...current, passes_enabled: saved.visitor_passes_enabled } : current));
      toast.success(
        saved.visitor_passes_enabled
          ? `Visitor passes are on at ${building?.name || 'this building'}.`
          : `Visitor passes are off at ${building?.name || 'this building'}. The front desk no longer sees them.`
      );
    } catch {
      toast.error('Could not change the visitor pass setting. Try again.');
    } finally {
      setSwitchBusy(false);
      load();
    }
  };

  const zone = data?.zone || '';
  const today = data?.today || '';
  const passesOn = data?.passes_enabled !== false;
  const approvalOn = data?.approval_required === true;
  const pendingCount = data?.pending_count ?? 0;
  // "Awaiting approval" is offered where it can mean something: approval is
  // on, something is waiting, or the reader is already looking at it.
  const stateOptions: Array<{ value: ListState; label: string }> = [
    { value: 'live', label: 'Live' },
    ...(approvalOn || pendingCount > 0 || listState === 'awaiting'
      ? [{ value: 'awaiting' as const, label: pendingCount ? `Awaiting approval (${pendingCount})` : 'Awaiting approval' }]
      : []),
    { value: 'ended', label: 'Ended' },
  ];
  const confirmFirst = confirming ? firstOf(confirming.pass) : '';

  const view: 'loading' | 'error' | 'empty' | 'ready' = !buildingId
    ? buildingsLoading && !fixedBuilding
      ? 'loading'
      : 'empty'
    : !data
    ? failed
      ? 'error'
      : 'loading'
    : rows.length
    ? 'ready'
    : 'empty';

  return (
    <Screen>
      <Lead>
        {fixedBuilding
          ? 'Every visitor pass at this building: who it is for, who added it (the building or a resident) and when they are next expected.'
          : 'Who has standing access to your buildings: every visitor pass, who added it and when they are next expected.'}
      </Lead>

      <Toolbar>
        {fixedBuilding ? null : (
        <ToolbarField>
          <ToolbarLabel htmlFor="passes-building">Building</ToolbarLabel>
          <TypeFilterSelect
            id="passes-building"
            value={buildingId ?? ''}
            disabled={buildingsLoading || buildings.length === 0}
            onChange={event => setBuildingId(event.target.value ? Number(event.target.value) : null)}
          >
            {buildingsLoading ? <option value="">Loading buildings…</option> : null}
            {!buildingsLoading && buildings.length === 0 ? <option value="">No buildings available</option> : null}
            {groups
              ? groups.map(group => (
                  <optgroup key={group.portfolio} label={group.portfolio}>
                    {group.buildings.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : buildings.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
          </TypeFilterSelect>
        </ToolbarField>
        )}
        <SegmentedControl<ListState>
          label="Which passes"
          options={stateOptions}
          value={listState}
          onChange={setListState}
        />
      </Toolbar>

      {buildingsError && !fixedBuilding ? <LoadError message={buildingsError} onRetry={() => window.location.reload()} /> : null}

      {canManage && buildingId && data ? (
        <SwitchCard>
          <SwitchLabel>
            <input
              type="checkbox"
              checked={passesOn}
              disabled={switchBusy}
              onChange={event => {
                if (event.target.checked) void setSwitch(true);
                else setConfirmOff(true);
              }}
            />
            Use visitor passes in this building
          </SwitchLabel>
          <SwitchHint aria-live="polite">
            {passesOn
              ? 'Turning this off hides every pass from the front desk until you turn it back on.'
              : 'Visitor passes are off here. The front desk sees no expected visitors, and nobody can add a pass, until you turn this back on.'}
          </SwitchHint>
          <SwitchLabel>
            <input
              type="checkbox"
              checked={approvalOn}
              disabled={switchBusy}
              onChange={event => void setApproval(event.target.checked)}
            />
            A manager approves passes residents add
          </SwitchLabel>
          <SwitchHint aria-live="polite">
            {approvalOn
              ? 'A pass a resident adds waits under Awaiting approval. Nobody is expected until a manager approves it.'
              : 'Passes residents add are active at once. The front desk sees them straight away.'}
          </SwitchHint>
        </SwitchCard>
      ) : null}

      {failed && data ? <LoadError message="Could not refresh the visitor passes." onRetry={load} /> : null}

      <StateSwitch state={view} minHeight={skeletonHeight(4, 72)}>
        {view === 'loading' ? (
          <ListSkeleton rows={4} rowHeight={72} label="visitor passes" />
        ) : view === 'error' ? (
          <LoadError message={failed || 'Could not load the visitor passes.'} onRetry={load} />
        ) : view === 'empty' ? (
          <Empty>
            <EmptyTitle>
              {!buildingId
                ? 'No building to show'
                : listState === 'live'
                ? `No live visitor passes at ${building?.name || 'this building'}`
                : listState === 'awaiting'
                ? `Nothing is waiting for approval at ${building?.name || 'this building'}`
                : `No ended visitor passes at ${building?.name || 'this building'}`}
            </EmptyTitle>
            <EmptyText>
              {!buildingId
                ? 'You do not have a building to look at yet.'
                : listState === 'awaiting'
                ? 'A pass a resident adds appears here when a manager approves passes at this building.'
                : listState === 'live'
                ? fixedBuilding
                  ? 'A pass is added by a manager, by the front desk from Expected today, or by the resident in their portal.'
                  : 'A pass is added from a resident on the Units screen, or by the resident in their portal.'
                : 'Passes that were ended, ran out or whose resident moved out appear here.'}
            </EmptyText>
          </Empty>
        ) : (
          <Staleable stale={loading}>
            <TableFrame>
              {/* The desk's copy reflows to cards below lg: eight columns in a
                  tablet-width desk column squeezed the visitor's name (J-4.10). */}
              <Table data-stack={fixedBuilding ? 'lg' : undefined}>
                <TableCaption>
                  Visitor passes at {building?.name || 'this building'}: the visitor, their unit and
                  resident, the schedule, who added it, the next visit and its state
                </TableCaption>
                <Thead>
                  <Tr>
                    <Th width={20}>Visitor</Th>
                    <Th width={8}>Unit</Th>
                    <Th width={14}>Resident</Th>
                    <Th width={20}>Schedule</Th>
                    <Th width={10}>Added by</Th>
                    <Th width={14}>Next visit</Th>
                    <Th width={10}>State</Th>
                    <Th width={14} style={{ textAlign: 'right' }}>
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Presence key={`${buildingId}:${listState}`}>
                    {rows.map((pass, index) => {
                      const name = nameOf(pass);
                      const pending = pass.state === 'pending';
                      const mayDecide = pass.can_decide === true && pending;
                      // A pending pass is answered, not paused (the server refuses it).
                      const mayEdit = pass.can_edit === true && pass.state !== 'ended' && !pending;
                      const mayEnd = pass.can_end === true && pass.state !== 'ended' && !mayDecide;
                      const paused = pass.state === 'paused';

                      return (
                        <BodyRow key={pass.pass_id} index={index % PAGE}>
                          <Td data-label="Visitor">
                            <NameButton type="button" onClick={() => setOpenPassId(pass.pass_id)}>
                              {name}
                            </NameButton>
                            {pass.company ? <Sub>{pass.company}</Sub> : null}
                          </Td>
                          <Td data-label="Unit">{pass.unit_label || '—'}</Td>
                          <Td data-label="Resident">{hostOf(pass)}</Td>
                          <Td data-label="Schedule">{describeSchedule(pass, { today })}</Td>
                          <Td data-label="Added by">{pass.created_via === 'staff' ? 'Building' : 'Resident'}</Td>
                          <Td data-label="Next visit">
                            {pass.next ? describeVisit(pass.next, zone, today) : '—'}
                          </Td>
                          <Td data-label="State">
                            <StatusPill tone={pass.state ? TONES[pass.state] : 'neutral'} dot>
                              {stateLabel(pass)}
                            </StatusPill>
                          </Td>
                          <Td data-label="Actions" style={{ textAlign: 'right' }}>
                            {mayDecide ? (
                              <RowActions>
                                <AppButton
                                  variant="secondary"
                                  size="compact"
                                  aria-label={`Approve ${possessive(name)} pass`}
                                  disabled={approving === pass.pass_id}
                                  onClick={async () => {
                                    setApproving(pass.pass_id);
                                    try {
                                      await decide(pass, 'approve');
                                    } finally {
                                      setApproving(null);
                                    }
                                  }}
                                >
                                  <StatusLabel
                                    state={approving === pass.pass_id ? 'busy' : 'idle'}
                                    labels={{ idle: 'Approve', busy: 'Approving…' }}
                                  />
                                </AppButton>
                                <AppButton
                                  variant="ghost"
                                  size="compact"
                                  aria-label={`Decline ${possessive(name)} pass`}
                                  onClick={() => {
                                    setDeclineNote('');
                                    setConfirming({ kind: 'decline', pass });
                                  }}
                                >
                                  Decline
                                </AppButton>
                              </RowActions>
                            ) : mayEdit || mayEnd ? (
                              <RowActions>
                                {!mayEdit ? null : paused ? (
                                  <AppButton
                                    variant="secondary"
                                    size="compact"
                                    aria-label={`Resume ${possessive(name)} pass`}
                                    disabled={resuming === pass.pass_id}
                                    onClick={async () => {
                                      setResuming(pass.pass_id);
                                      try {
                                        await runAction(pass, 'resume');
                                      } finally {
                                        setResuming(null);
                                      }
                                    }}
                                  >
                                    <StatusLabel
                                      state={resuming === pass.pass_id ? 'busy' : 'idle'}
                                      labels={{ idle: 'Resume', busy: 'Resuming…' }}
                                    />
                                  </AppButton>
                                ) : (
                                  <AppButton
                                    variant="secondary"
                                    size="compact"
                                    aria-label={`Pause ${possessive(name)} pass`}
                                    onClick={() => setConfirming({ kind: 'pause', pass })}
                                  >
                                    Pause
                                  </AppButton>
                                )}
                                {mayEnd ? (
                                  <AppButton
                                    variant="ghost"
                                    size="compact"
                                    aria-label={`End ${possessive(name)} pass`}
                                    onClick={() => setConfirming({ kind: 'end', pass })}
                                  >
                                    End
                                  </AppButton>
                                ) : null}
                              </RowActions>
                            ) : (
                              '—'
                            )}
                          </Td>
                        </BodyRow>
                      );
                    })}
                  </Presence>
                </Tbody>
              </Table>
            </TableFrame>
          </Staleable>
        )}
      </StateSwitch>

      {view === 'ready' ? (
        <>
          {moreFailed ? <LoadError message="Could not load more visitor passes." onRetry={loadMore} /> : null}
          <Footer>
            {data?.next_cursor ? (
              <AppButton variant="ghost" onClick={loadMore} loading={loadingMore} loadingLabel="Loading more">
                Show more
              </AppButton>
            ) : null}
            <FooterNote role="status">
              {data?.next_cursor ? `Showing ${rows.length}` : `All ${rows.length} shown`}
              {/* A pass is a schedule on the BUILDING's wall clock ("Tuesdays
                  09:00–13:00"), like a desk day: it belongs to the building and
                  is shown on its clock for everybody — and says so. */}
              {zone && zoneLabel(zone) ? ` · Building time — ${zoneLabel(zone)}` : ''}
            </FooterNote>
          </Footer>
        </>
      ) : null}

      <PassDetailModal
        passId={openPassId}
        open={openPassId !== null}
        onClose={() => setOpenPassId(null)}
        onChanged={load}
      />

      <ConfirmDialog
        open={confirming?.kind === 'pause'}
        title={`Pause ${possessive(confirmFirst)} pass?`}
        message={`The front desk won't see ${confirmFirst} on the expected list until the pass is resumed.`}
        primaryLabel="Pause pass"
        dismissLabel="Never mind"
        onPrimary={async () => {
          if (confirming) await runAction(confirming.pass, 'pause');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming?.kind === 'end'}
        title={`End ${possessive(confirmFirst)} pass?`}
        message={`The front desk will stop expecting ${confirmFirst}. An ended pass can't be restarted; add a new one if they need it again.`}
        primaryLabel="End pass"
        primaryVariant="destructive"
        dismissLabel="Keep pass"
        onPrimary={async () => {
          if (confirming) await runAction(confirming.pass, 'revoke');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming?.kind === 'decline'}
        title={`Decline ${possessive(confirmFirst)} pass?`}
        message={`The front desk won't expect ${confirmFirst}. The household is told, and reads your note on the pass.`}
        primaryLabel="Decline pass"
        primaryVariant="destructive"
        dismissLabel="Keep waiting"
        onPrimary={async () => {
          if (confirming) await decide(confirming.pass, 'reject', declineNote);
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      >
        <CountedTextarea
          id="decline-note"
          label="Note to the household (optional)"
          value={declineNote}
          max={data?.limits?.note_max ?? 280}
          rows={3}
          onChange={event => setDeclineNote(event.target.value)}
        />
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmOff}
        title={`Turn visitor passes off at ${building?.name || 'this building'}?`}
        message="Every pass is hidden from the front desk, and nobody can add one, until you turn this back on. The passes themselves are kept."
        primaryLabel="Turn off"
        dismissLabel="Keep on"
        onPrimary={async () => {
          await setSwitch(false);
          setConfirmOff(false);
        }}
        onDismiss={() => setConfirmOff(false)}
      />
    </Screen>
  );
};
